// The bridge between the app registry and the sidebar engine.
//
// Every app's `AppDefinition.sidebar` panels are indexed by panel id here, so
// `renderSidebarPanel` can ask one question — "is this panel object-driven?" —
// and fall through to the remaining hand-written panels when it isn't. Adding a
// panel to an app's config is enough to make it render; nothing else is wired.

import type { PanelConfig } from '@clarix/shared-types';
import { APP_REGISTRY } from '../apps';
import type {
  SidebarProviderRegistry,
  SidebarSectionProviderRegistry,
} from './engine/SidebarEngine';
import { useProjectsTreeProvider, useTimesheetReviewProvider } from './providers/projectsProviders';
import { useWorkspaceMastersProvider } from './providers/workspaceProviders';
import { useCalendarAppUpcomingProvider } from './providers/calendarProviders';
import { useSupportTeamsProvider, useSupportUsersProvider } from './providers/supportProviders';
import {
  useCrmCommissionSections,
  useCrmMasterSections,
  useCrmOperationsSections,
  useCrmUsersProvider,
} from './providers/crmProviders';

/** panelId → config, across every app that ships an object-driven sidebar. */
const PANELS: Map<string, PanelConfig> = new Map(
  APP_REGISTRY.flatMap((app) => app.sidebar?.panels ?? []).map((panel) => [panel.id, panel]),
);

export function getDeclarativePanel(panelId: string): PanelConfig | undefined {
  return PANELS.get(panelId);
}

/**
 * panelId → header title, for every app that declares one. Handed to
 * `<AppShell panelTitles>` so the layout names a panel from its config instead
 * of a hardcoded map. Panels with no `title` fall through to the layout's
 * legacy map and then the rail item's own label.
 */
export const PANEL_TITLES: Readonly<Record<string, string>> = Object.fromEntries(
  [...PANELS.values()].flatMap((p) => (p.title ? [[p.id, p.title]] : [])),
);

/**
 * Named providers a config can point a section at. Each is a hook returning
 * already-shaped rows for data no declarative `{ api, map }` can express.
 */
export const SIDEBAR_PROVIDERS: SidebarProviderRegistry = {
  projectsTree: useProjectsTreeProvider,
  timesheetReview: useTimesheetReviewProvider,
  calendarAppUpcoming: useCalendarAppUpcomingProvider,
  workspaceMasters: useWorkspaceMastersProvider,
  supportTeams: useSupportTeamsProvider,
  supportUsers: useSupportUsersProvider,
  crmUsers: useCrmUsersProvider,
};

/**
 * Providers that produce whole SECTIONS, for panels whose groups are data — the
 * CRM link panels (bucketed by `group`) and its master-config catalogue.
 */
export const SIDEBAR_SECTION_PROVIDERS: SidebarSectionProviderRegistry = {
  crmOperations: useCrmOperationsSections,
  crmCommission: useCrmCommissionSections,
  crmMaster: useCrmMasterSections,
};
